import { ExternalLink, FileText, Pencil, Trash2 } from 'lucide-react'
import React from 'react'

import { navigateTo } from '../../../common/frontend/route-utils.js'
import { t } from '../../../common/i18n.js'
import {
    SignatureInsertWithRows,
    SignatureItem,
    SignatureUpdate,
} from '../../../common/types/api.js'
import {
    copySignatureHTML,
    deleteSignature,
    editSignature,
} from '../../actions/signature-actions.js'
import nopeLogo from '../../assets/logos/NOPE-logo-white.svg'
import nopeLogoBig from '../../assets/logos/NOPE-logo-white_big.svg'
import playLogo from '../../assets/logos/play-logo-white.svg'
import playLogoBig from '../../assets/logos/play-logo-white_big.svg'
import { Button } from '../../components/button/button.js'
import { PageHeaderProps } from '../../components/page-header/page-header.js'
import { ResponsiveLogo } from '../../components/responsive-logo/responsive-logo.js'
import { SignatureProps } from '../../components/signature/signature.js'
import { httpUpload } from '../../http-utils.js'
import { CardColumnsProps } from '../../modules/card-columns/card-columns.js'
import { SignatureCreationProps } from '../../modules/signature-creation/signature-creation.js'
import { View } from '../../types.js'
import { handleError } from '../../utils/error-utils.js'
import { reloadSignatures } from '../../utils/load-utils.js'
import { notify } from '../../utils/notification-utils.js'
import {
    formDataToSignatureInsertWithRows,
    formDataToSignatureUpdate,
    SignatureFormData,
    validateSignatureFormData,
} from '../../utils/signature-form-utils.js'
import {
    createSignatureCreationProps,
    getInitialSignatureFormData,
    getSignatureErrorMessage,
    groupSignaturesByCompany,
} from './helpers.js'

export const getSignatureToolHeroProps = (view: View): PageHeaderProps => {
    const lang = view.state.lang
    return {
        title: t.signature.headerTitle(lang),
        actions: React.createElement(Button, {
            text: t.signature.installGuide(lang),
            iconRight: ExternalLink,
            href: 'https://sites.google.com/play.ee/space/juhendid/signatuur-allkiri',
            variant: 'link',
            size: 'header',
        }),
    }
}

export const getSignatureCreationHeroProps = (view: View): PageHeaderProps => {
    const lang = view.state.lang
    return {
        title: t.signature.headerTitle(lang),
        actions: React.createElement(Button, {
            text: t.signature.installGuide(lang),
            iconRight: ExternalLink,
            href: 'https://sites.google.com/play.ee/space/juhendid/signatuur-allkiri',
            variant: 'link',
            size: 'header',
        }),
    }
}

export const getSignatureToolCardColumnsProps = (
    view: View,
    onReload: (view: View) => void = reloadSignatures,
): CardColumnsProps => {
    const lang = view.state.lang
    const s = t.signature
    const signatures = view.state.signatures

    if (!signatures.data || !Array.isArray(signatures.data?.data)) {
        return { categories: [] }
    }

    const { playSignatures, nopeSignatures } = groupSignaturesByCompany(signatures.data.data)

    const convertSignatureToCard = (sig: SignatureItem) => {
        const signatureRefObject: React.RefObject<HTMLTableElement | null> = React.createRef()

        const signatureRef = (element: HTMLTableElement | null) => {
            signatureRefObject.current = element
        }

        const signatureProps: SignatureProps = {
            name: sig.first_and_last_name || '',
            title: sig.position || '',
            email: sig.email || undefined,
            imageUrl: sig.image || undefined,
            customLines:
                sig.rows?.map((row) => ({
                    key: row.label || undefined,
                    value: row.value || undefined,
                    link: row.url || undefined,
                })) || [],
            playLogo: sig.play_logo,
            nopeLogo: sig.nope_logo,
            allianceLogo: sig.alliance_logo,
            isPlay: sig.company === 'play',
            isNope: sig.company === 'nope',
        }

        return {
            signature: { ...signatureProps, signatureRef },
            buttons: [
                {
                    text: s.copy(lang),
                    variant: 'primary' as const,
                    size: 'medium' as const,
                    icon: FileText,
                    onClick: async () => {
                        await copySignatureHTML(view, signatureRefObject, signatureProps)
                    },
                },
                {
                    text: s.edit(lang),
                    variant: 'secondary' as const,
                    size: 'medium' as const,
                    icon: Pencil,
                    onClick: () => {
                        editSignature(view, sig.id.toString())
                        onReload(view)
                    },
                },
                {
                    text: s.deleteSignature(lang),
                    variant: 'alert' as const,
                    size: 'medium' as const,
                    icon: Trash2,
                    onClick: async () => {
                        await deleteSignature(view, sig.id.toString())
                        onReload(view)
                    },
                },
            ],
        }
    }

    const categories = [
        {
            header: React.createElement(ResponsiveLogo, {
                src: playLogo,
                srcBig: playLogoBig,
                alt: s.playLogoAlt(lang),
            }),
            buttonText: s.addPlaySignature(lang),
            buttonOnClick: async () => {
                navigateTo('/signaturetool/create/play')
            },
            cards: playSignatures.map((sig) => convertSignatureToCard(sig)),
        },
        {
            header: React.createElement(ResponsiveLogo, {
                src: nopeLogo,
                srcBig: nopeLogoBig,
                alt: s.nopeLogoAlt(lang),
            }),
            buttonText: s.addNopeSignature(lang),
            buttonOnClick: async () => {
                navigateTo('/signaturetool/create/nope')
            },
            cards: nopeSignatures.map((sig) => convertSignatureToCard(sig)),
        },
    ]

    return { categories }
}

export const getSignatureCreationViewProps = (
    view: View,
    mode: 'create' | 'edit',
    signatureId?: string,
    company?: 'play' | 'nope',
): { hero: PageHeaderProps; signatureCreation: SignatureCreationProps } => {
    const lang = view.state.lang
    const s = t.signature
    const handleSave = async (formData: SignatureFormData) => {
        const validationError = validateSignatureFormData(formData, lang)
        if (validationError) {
            notify({
                view,
                type: 'error',
                text: validationError.message,
            })
            return
        }

        let imageUrl = formData.imageUrl
        if (formData.imageUrl?.startsWith('blob:')) {
            try {
                const response = await fetch(formData.imageUrl)
                const blob = await response.blob()
                const uploadResult = await httpUpload(view.state, '/upload/signature-image', blob)
                imageUrl = uploadResult.imageUrl
            } catch (uploadError) {
                notify({
                    view,
                    type: 'error',
                    text: s.imageUploadError(lang),
                })
                return
            }
        }

        try {
            const finalFormData: SignatureFormData = {
                ...formData,
                imageUrl,
            }

            if (mode === 'create') {
                const signatureData: SignatureInsertWithRows =
                    formDataToSignatureInsertWithRows(finalFormData)
                await view.api.signatures.createSignature(signatureData)
                notify({ view, type: 'success', text: s.signatureCreated(lang) })
            } else if (mode === 'edit' && signatureId) {
                const signatureData: SignatureUpdate = formDataToSignatureUpdate(
                    finalFormData,
                    Number(signatureId),
                )
                await view.api.signatures.updateSignature(signatureId, signatureData)
                notify({
                    view,
                    type: 'success',
                    text: s.signatureSaved(lang),
                    additionalText: s.signatureSavedHint(lang),
                })
            }

            reloadSignatures(view)
            navigateTo('/signaturetool')
        } catch (error: unknown) {
            handleError(view, error)
            notify({
                view,
                type: 'error',
                text: getSignatureErrorMessage(error, mode, lang),
            })
        }
    }

    const navigateToSignatureTool = () => {
        navigateTo('/signaturetool')
    }

    const initialData = getInitialSignatureFormData(view, mode, signatureId, company)

    const signatureCreationProps = createSignatureCreationProps(
        mode,
        initialData,
        view.state,
        handleSave,
        navigateToSignatureTool,
        navigateToSignatureTool,
        lang,
    )

    return {
        hero: getSignatureCreationHeroProps(view),
        signatureCreation: signatureCreationProps,
    }
}
